/**
 * Student list for a study group. Most students take the full assignment;
 * the "Learning to read & write" toggle reveals stepped-down reading and
 * writing levels for the ones who need them.
 */

import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { bibleState } from '@/state/bible-state';
import { TrashIcon } from '@components/icons';
import {
  readingLevelToDisplayText,
  writingLevelToDisplayText,
  READING_LEVELS,
  WRITING_LEVELS,
  FULL_READING_LEVEL,
  FULL_WRITING_LEVEL,
  LEARNING_READING_LEVEL,
  LEARNING_WRITING_LEVEL,
  isLearningLevels,
  type ReadingLevel,
  type WritingLevel,
} from '@skjv/common';

interface GroupStudentsProps {
  groupId: string;
}

export const GroupStudents = observer(function GroupStudents({ groupId }: GroupStudentsProps) {
  const group = bibleState.getStudyGroup(groupId);
  const [isAdding, setIsAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newIsLearning, setNewIsLearning] = useState(false);
  const [newReadingLevel, setNewReadingLevel] = useState<ReadingLevel>(LEARNING_READING_LEVEL);
  const [newWritingLevel, setNewWritingLevel] = useState<WritingLevel>(LEARNING_WRITING_LEVEL);

  if (!group) return null;

  const resetAddForm = () => {
    setIsAdding(false);
    setNewName('');
    setNewIsLearning(false);
    setNewReadingLevel(LEARNING_READING_LEVEL);
    setNewWritingLevel(LEARNING_WRITING_LEVEL);
  };

  const submitAdd = () => {
    if (!newName.trim()) return;
    bibleState.addStudentToGroup(groupId, {
      name: newName.trim(),
      readingLevel: newIsLearning ? newReadingLevel : FULL_READING_LEVEL,
      writingLevel: newIsLearning ? newWritingLevel : FULL_WRITING_LEVEL,
    });
    setNewName('');
  };

  return (
    <>
      {group.students.length === 0 ? (
        <p className="text-sm italic text-stone-500 dark:text-stone-400">
          No students in this group
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {group.students.map((student) => {
            const isLearning = isLearningLevels(student.readingLevel, student.writingLevel);
            return (
              <div key={student.id} className="flex items-start justify-between gap-2 py-1 pl-6">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm text-stone-700 dark:text-stone-300">
                    {student.name}
                  </div>
                  <label className="mt-0.5 flex cursor-pointer items-center gap-1.5 text-xs text-stone-500 dark:text-stone-400">
                    <input
                      type="checkbox"
                      checked={isLearning}
                      onChange={(e) =>
                        bibleState.updateStudent(groupId, student.id, {
                          readingLevel: e.target.checked
                            ? LEARNING_READING_LEVEL
                            : FULL_READING_LEVEL,
                          writingLevel: e.target.checked
                            ? LEARNING_WRITING_LEVEL
                            : FULL_WRITING_LEVEL,
                        })
                      }
                      className="h-3.5 w-3.5 rounded border-stone-300 text-cyan-500 focus:ring-cyan-500"
                    />
                    Learning to read &amp; write
                  </label>
                  {isLearning && (
                    <div className="mt-1 flex gap-1.5">
                      <LevelSelect
                        value={student.readingLevel}
                        options={READING_LEVELS}
                        toDisplayText={readingLevelToDisplayText}
                        label="Reading level"
                        onChange={(level) =>
                          bibleState.updateStudent(groupId, student.id, { readingLevel: level })
                        }
                      />
                      <LevelSelect
                        value={student.writingLevel}
                        options={WRITING_LEVELS}
                        toDisplayText={writingLevelToDisplayText}
                        label="Writing level"
                        onChange={(level) =>
                          bibleState.updateStudent(groupId, student.id, { writingLevel: level })
                        }
                      />
                    </div>
                  )}
                </div>
                <button
                  onClick={() => bibleState.removeStudentFromGroup(groupId, student.id)}
                  className="p-1 text-stone-400 transition-colors hover:text-red-500"
                  title="Remove student"
                >
                  <TrashIcon className="h-3 w-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {isAdding ? (
        <div className="mt-2 flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Student name..."
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newName.trim()) submitAdd();
                if (e.key === 'Escape') resetAddForm();
              }}
              className="flex-1 rounded-lg border border-stone-300 bg-white px-2.5 py-1.5 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 dark:border-stone-600 dark:bg-stone-800 dark:text-stone-200"
            />
            <button
              onClick={submitAdd}
              disabled={!newName.trim()}
              className="rounded-lg bg-cyan-500 px-2.5 py-1.5 text-xs text-white transition-colors hover:bg-cyan-600 disabled:opacity-50"
            >
              Add
            </button>
            <button
              onClick={resetAddForm}
              className="text-xs text-stone-500 hover:text-stone-700 dark:text-stone-400 dark:hover:text-stone-200"
            >
              Done
            </button>
          </div>
          <label className="flex cursor-pointer items-center gap-1.5 text-xs text-stone-500 dark:text-stone-400">
            <input
              type="checkbox"
              checked={newIsLearning}
              onChange={(e) => setNewIsLearning(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-stone-300 text-cyan-500 focus:ring-cyan-500"
            />
            Learning to read &amp; write
          </label>
          {newIsLearning && (
            <div className="flex gap-1.5">
              <LevelSelect
                value={newReadingLevel}
                options={READING_LEVELS}
                toDisplayText={readingLevelToDisplayText}
                label="Reading level"
                onChange={setNewReadingLevel}
              />
              <LevelSelect
                value={newWritingLevel}
                options={WRITING_LEVELS}
                toDisplayText={writingLevelToDisplayText}
                label="Writing level"
                onChange={setNewWritingLevel}
              />
            </div>
          )}
        </div>
      ) : (
        <button
          onClick={() => setIsAdding(true)}
          data-guide="add-student"
          className="mt-2 text-sm text-cyan-600 hover:underline dark:text-cyan-400"
        >
          + Add student
        </button>
      )}
    </>
  );
});

function LevelSelect<T extends string>({
  value,
  options,
  toDisplayText,
  label,
  onChange,
}: {
  value: T;
  options: readonly T[];
  toDisplayText: (level: T) => string;
  label: string;
  onChange: (level: T) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as T)}
      onClick={(e) => e.stopPropagation()}
      aria-label={label}
      title={label}
      className="mt-0.5 w-full max-w-[9rem] cursor-pointer rounded-md border border-stone-200 bg-transparent px-1.5 py-0.5 text-xs text-stone-500 hover:border-stone-300 focus:border-cyan-400 focus:outline-none focus:ring-1 focus:ring-cyan-400 dark:border-stone-700 dark:text-stone-400 dark:hover:border-stone-600"
    >
      {options.map((level) => (
        <option key={level} value={level}>
          {toDisplayText(level)}
        </option>
      ))}
    </select>
  );
}
